import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import { setUserAsAdmin, removeAdminStatus } from '../../services/admin';
import LoadingSpinner from '../loading-spinner/loading-spinner';
import ErrorDisplay from '../error-display/error-display';

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [updatingUser, setUpdatingUser] = useState(null);
  const { currentUser, isAdmin } = useAuth();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const usersList = [];
      querySnapshot.forEach((doc) => {
        usersList.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setUsers(usersList);
    } catch (err) {
      setError('Error fetching users: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAdmin = async (userId, currentAdminStatus) => {
    if (!isAdmin) {
      setError('You do not have permission to modify user roles');
      return;
    }

    if (userId === currentUser.uid && currentAdminStatus) {
      setError('You cannot remove admin status from yourself');
      return;
    }

    setUpdatingUser(userId);
    setError('');
    setSuccess('');

    try {
      let success;
      if (currentAdminStatus) {
        success = await removeAdminStatus(userId);
        if (success) {
          setSuccess('Admin status removed successfully');
        }
      } else {
        success = await setUserAsAdmin(userId);
        if (success) {
          setSuccess('User promoted to admin successfully');
        }
      }

      if (success) {
        // Update the local state
        setUsers(prevUsers => 
          prevUsers.map(user => 
            user.id === userId 
              ? { ...user, isAdmin: !currentAdminStatus }
              : user
          )
        );

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to update user role');
      }
    } catch (err) {
      setError('Error updating user role: ' + err.message);
    } finally {
      setUpdatingUser(null);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return timestamp.toDate().toLocaleDateString();
    } catch {
      return 'N/A';
    }
  };

  if (loading) {
    return <LoadingSpinner text="Loading users..." />;
  }

  return (
    <div>
      {error && <ErrorDisplay message={error} />}
      {success && <ErrorDisplay message={success} type="info" />}
      
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>All Users ({users.length})</h3>
        <button 
          className="btn btn-outline-primary"
          onClick={fetchUsers}
          disabled={loading}
        >
          Refresh
        </button>
      </div>

      {users.length === 0 ? (
        <div className="alert alert-info">
          <p className="mb-0">No users found.</p>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead>
              <tr>
                <th>Email</th>
                <th>Display Name</th>
                <th>Admin Status</th>
                <th>Created Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.email}</td>
                  <td>{user.displayName || 'N/A'}</td>
                  <td>
                    <span className={`badge ${user.isAdmin ? 'bg-success' : 'bg-secondary'}`}>
                      {user.isAdmin ? 'Admin' : 'User'}
                    </span>
                  </td>
                  <td>{formatDate(user.createdAt)}</td>
                  <td>
                    <button
                      className={`btn btn-sm ${user.isAdmin ? 'btn-outline-danger' : 'btn-outline-success'}`}
                      onClick={() => handleToggleAdmin(user.id, user.isAdmin)}
                      disabled={updatingUser === user.id || (user.id === currentUser.uid && user.isAdmin)}
                    >
                      {updatingUser === user.id ? (
                        <LoadingSpinner size="small" />
                      ) : (
                        user.isAdmin ? 'Remove Admin' : 'Make Admin'
                      )}
                    </button>
                    {user.id === currentUser.uid && (
                      <span className="badge bg-primary ms-2">You</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UserList;